<template>
    <div class="thinking-dots" aria-hidden="true">
        <span v-for="stop in DOT_COUNT" :key="stop" class="thinking-dot"></span>
    </div>
</template>

<script setup lang="ts">
// One dot per stop on the `--color-chat-thinking-dot-*` cycle (tokens/chat-progress-dots.css).
const DOT_COUNT = 7;
</script>

<style scoped>
@reference "../../css/tailwind.css";

/*
 * Ported from the Work IQ coaching demo's intelligence layer
 * (valence-builders/demos-and-storytelling, apps/nadia-demo). Dots slide in from the right one
 * after another, each running the full colour cycle and landing on the settled magenta.
 *
 * The demo drives this with JS timers that add and remove classes; here the cycle's own last
 * keyframe is the settled colour, so per-dot animation delays do the same work in CSS with no
 * timers to keep in step with the render. The demo's closing collapse is left out: it exists to
 * hand off to the next scripted step, whereas a live turn moves on when the next tool call
 * actually starts, which the label crossfade already covers.
 *
 * A live step can also run far longer than the demo's scripted 1.05s, so once the one-shot lands
 * the row keeps replaying the last leg of the cycle, the change into the settled colour, as a
 * wave on the same stagger. The scripted version has no need for it and simply holds.
 */
@layer components {
    .thinking-dots {
        @apply inline-flex items-center gap-1 align-middle;
    }

    .thinking-dot {
        @apply size-2.5 shrink-0 rounded-full opacity-0;
        box-shadow: inset 0 0 0 1px var(--color-chat-thinking-dot-ring);

        /* The starting colour each dot wears until its cycle picks up. */
        &:nth-child(1) {
            background-color: var(--color-chat-thinking-dot-1);
        }
        &:nth-child(2) {
            background-color: var(--color-chat-thinking-dot-2);
        }
        &:nth-child(3) {
            background-color: var(--color-chat-thinking-dot-3);
        }
        &:nth-child(4) {
            background-color: var(--color-chat-thinking-dot-4);
        }
        &:nth-child(5) {
            background-color: var(--color-chat-thinking-dot-5);
        }
        &:nth-child(6) {
            background-color: var(--color-chat-thinking-dot-6);
        }
        &:nth-child(7) {
            background-color: var(--color-chat-thinking-dot-7);
        }

        @media (prefers-reduced-motion: no-preference) {
            --dot-in: 250ms;
            --dot-stagger: 55ms;
            --dot-cycle-hold: 300ms;
            --dot-cycle: 1s;
            --dot-settle-wave: 1800ms;
            --dot-enters-at: calc(var(--dot-index) * var(--dot-stagger));

            /*
             * Three legs, each picking up where the last leaves off: the dot arrives, cycles the
             * palette once, then waves into the settled colour for as long as the step runs. The
             * wave is last in the list, so it owns the colour once it starts while the cycle's
             * `forwards` fill holds it until then.
             */
            animation:
                thinking-dot-in var(--dot-in) ease var(--dot-enters-at) forwards,
                thinking-dot-cycle var(--dot-cycle) ease calc(var(--dot-enters-at) + var(--dot-cycle-hold)) forwards,
                thinking-dot-settle-wave var(--dot-settle-wave) ease-in-out calc(var(--dot-enters-at) + var(--dot-cycle-hold) + var(--dot-cycle)) infinite;

            &:nth-child(1) {
                --dot-index: 0;
            }
            &:nth-child(2) {
                --dot-index: 1;
            }
            &:nth-child(3) {
                --dot-index: 2;
            }
            &:nth-child(4) {
                --dot-index: 3;
            }
            &:nth-child(5) {
                --dot-index: 4;
            }
            &:nth-child(6) {
                --dot-index: 5;
            }
            &:nth-child(7) {
                --dot-index: 6;
            }
        }

        /* No entrance to play, so the row is simply present in its settled state. */
        @media (prefers-reduced-motion: reduce) {
            @apply opacity-100;
            background-color: var(--color-chat-thinking-dot-settled);
        }
    }
}

@keyframes thinking-dot-in {
    from {
        opacity: 0;
        transform: translateX(16px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* The cycle's closing leg on repeat: away from the settled colour and back into it. */
@keyframes thinking-dot-settle-wave {
    0%,
    100% {
        background-color: var(--color-chat-thinking-dot-settled);
    }

    45% {
        background-color: var(--color-chat-thinking-dot-6);
    }
}

@keyframes thinking-dot-cycle {
    0% {
        background-color: var(--color-chat-thinking-dot-1);
    }
    15% {
        background-color: var(--color-chat-thinking-dot-2);
    }
    30% {
        background-color: var(--color-chat-thinking-dot-3);
    }
    45% {
        background-color: var(--color-chat-thinking-dot-4);
    }
    60% {
        background-color: var(--color-chat-thinking-dot-5);
    }
    75% {
        background-color: var(--color-chat-thinking-dot-6);
    }
    100% {
        background-color: var(--color-chat-thinking-dot-settled);
    }
}
</style>
